import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { apiFetch } from "../services/api";
import { User } from "../types";
export default function DashboardPage({ user }: { user: User }) {
  const [data, setData] = useState<any>(null),
    [error, setError] = useState("");
  useEffect(() => {
    apiFetch("/api/workspace/summary")
      .then(async (r) => {
        if (!r.ok) throw new Error();
        setData(await r.json());
      })
      .catch(() =>
        setError("Unable to load your overview. Refresh to try again."),
      );
  }, []);
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">YOUR WORKSPACE</span>
          <h1>A fresh opportunity awaits.</h1>
          <p>Find the right people. Build a list worth reaching out to.</p>
        </div>
        <Link className="button button-dark" to="/search">
          Find leads <span>↗</span>
        </Link>
      </div>
      {error && <p className="notice error">{error}</p>}
      <div className="stat-grid">
        {[
          [
            "✦",
            "Available credits",
            user.credits.toLocaleString(),
            "Unlock your next connection",
          ],
          [
            "⌑",
            "Unlocked contacts",
            data?.unlocked?.toLocaleString() ?? "—",
            "Ready for your next step",
          ],
          [
            "▤",
            "Your lists",
            data?.lists ?? "—",
            "Organize by client or campaign",
          ],
          [
            "☆",
            "Saved searches",
            data?.searches ?? "—",
            "Pick up where you left off",
          ],
        ].map(([icon, label, value, detail]) => (
          <article className="stat-card" key={label}>
            <span className="stat-icon">{icon}</span>
            <span>{label}</span>
            <strong>{value}</strong>
            <small>{detail}</small>
          </article>
        ))}
      </div>
      <div className="dashboard-columns">
        <section className="panel getting-started">
          <span className="eyebrow">YOUR NEXT STEPS</span>
          <h2>
            A little structure.
            <br />A lot of possibility.
          </h2>
          {[
            [
              "1",
              "Explore the database",
              "Use filters to find people who fit your market.",
              "/search",
            ],
            [
              "2",
              "Build your shortlist",
              "Save contacts to a named list, then unlock the details.",
              "/my-leads",
            ],
            [
              "3",
              "Take your contacts with you",
              "Download unlocked contacts as a CSV.",
              "/downloads",
            ],
          ].map(([n, title, description, url]) => (
            <Link className="next-step" to={url} key={n}>
              <span>{n}</span>
              <div>
                <strong>{title}</strong>
                <p>{description}</p>
              </div>
              <b>↗</b>
            </Link>
          ))}
        </section>
        <section className="panel">
          <div className="panel-heading">
            <h2>Credit activity</h2>
            <Link to="/billing">Billing ↗</Link>
          </div>
          {data?.ledger?.length ? (
            data.ledger.map((item: any, i: number) => (
              <div className="activity-row" key={i}>
                <span className="activity-icon">
                  {item.delta > 0 ? "↙" : "↗"}
                </span>
                <div>
                  <strong>
                    {item.reason === "lead_unlock"
                      ? "Contact unlocked"
                      : item.reason === "subscription_renewal"
                        ? "Plan credits received"
                        : "Credit adjustment"}
                  </strong>
                  <small>
                    {new Date(item.created_at + "Z").toLocaleDateString()}
                  </small>
                </div>
                <b className={item.delta > 0 ? "positive" : ""}>
                  {item.delta > 0 ? "+" : ""}
                  {item.delta}
                </b>
              </div>
            ))
          ) : (
            <div className="empty-state compact">
              <span>✦</span>
              <h3>Your next connection starts here</h3>
              <p>
                Your credit history will appear after your first subscription or
                unlock.
              </p>
              <Link to="/pricing">Explore plans →</Link>
            </div>
          )}
        </section>
      </div>
      <div className="inventory-banner">
        <span>◈</span>
        <div>
          <strong>Explore the ReachAtlas database</strong>
          <p>
            {data
              ? `${data.contacts.toLocaleString()} available contacts across ${data.companies.toLocaleString()} companies.`
              : "Your shared source for business contacts."}{" "}
            Inventory grows as new records are added.
          </p>
        </div>
        <Link className="button button-outline" to="/search">
          Explore contacts ↗
        </Link>
      </div>
    </>
  );
}
